import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import PaidIcon from "@mui/icons-material/Paid";
import ReceiptIcon from "@mui/icons-material/Receipt";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import moment from "moment";
import SwipeableCard from "./SwipeableCard";
import {
  getWithdrawalWalletDisplay,
  getWithdrawalWalletTooltip,
} from "../../Utils/cryptoWalletUtils";
import { isCapitalRoutingRow } from "../../Utils/fundsPeriodTotals";
import CapitalRoutingChip from "../CapitalRoutingChip/CapitalRoutingChip";

/** E10: on-chain explorer URL for an auto-pay payout, or null if no tx hash. */
const EXPLORER_TX_BASE = {
  POLYGON: "https://polygonscan.com/tx/",
  ETHEREUM: "https://etherscan.io/tx/",
  BSC: "https://bscscan.com/tx/",
};
const getExplorerTxUrl = (row) => {
  const txHash = String(row?.auto_pay_tx_hash || "").trim();
  if (!txHash) return null;
  const network = String(row?.selected_wallet_network || "POLYGON").toUpperCase();
  const base = EXPLORER_TX_BASE[network] || EXPLORER_TX_BASE.POLYGON;
  return `${base}${txHash}`;
};

const MobileCardView = ({
  rows,
  loading,
  onApprove,
  onReject,
  onPay,
  onProcess,
  onConditionalOffer,
  type = "deposit", // "deposit" or "withdrawal"
  withdrawalThreshold = 80,
  processingId = null,
}) => {
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!rows || rows.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
          p: 3,
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No transactions found
        </Typography>
      </Box>
    );
  }

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "--";
    const seconds = date?.seconds;
    const nanoseconds = date?.nanoseconds;
    const milliseconds = seconds * 1000 + Math.floor(nanoseconds / 1000000);
    return moment(milliseconds).format("DD/MM/YYYY hh:mm A");
  };

  const getStatusInfo = (row) => {
    const { request_status, profit_loss_percent } = row;

    if (type === "deposit") {
      switch (request_status) {
        case 0:
          return { label: "Pending", color: "#ff9800", action: "process" };
        case 1:
          return { label: "Waiting", color: "#2196F3", action: "approve" };
        case 2:
          return { label: "Approved", color: "#4CAF50", action: null };
        case 3:
          return {
            label: "Cancelled By User",
            color: "#F44336",
            action: null,
          };
        case 4:
          return { label: "Rejected", color: "#D32F2D", action: null };
        default:
          return { label: "Unknown", color: "#757575", action: null };
      }
    } else {
      // withdrawal
      if (request_status === 0) {
        return {
          label: "OTP verification Pending",
          color: "orange",
          action: null,
        };
      }
      if (request_status === 1 && isCapitalRoutingRow(row)) {
        return { label: "In transit", color: "#0288D1", action: null };
      }
      if (request_status === 1 && profit_loss_percent !== 0) {
        return {
          label: "Awaiting User Action",
          color: "#F44336",
          action: null,
        };
      }
      if (request_status === 1 && row?.auto_pay_queued === true) {
        return {
          label: "🕐 Queued",
          color: "#6A1B9A",
          action: null,
        };
      }
      if (request_status === 1 && profit_loss_percent === 0) {
        // Check for conditional offer
        const withdrawalAmount = parseFloat(row?.total_amount) || 0;
        const originalBalance = parseFloat(row?.original_balance) || 0;
        const currentBalance = parseFloat(row?.net_balance) || 0;
        const balanceToUse = originalBalance > 0 ? originalBalance : currentBalance;
        const percentage =
          balanceToUse > 0 ? (withdrawalAmount / balanceToUse) * 100 : 0;
        const exceedsThreshold = percentage >= withdrawalThreshold;
        const conditionalOfferSent = row?.conditional_offer_sent === true;
        const thresholdTriggered = row?.threshold_triggered === true;

        if ((exceedsThreshold || thresholdTriggered) && !conditionalOfferSent) {
          return {
            label: "Processing - Conditional Offer",
            color: "#FF9800",
            action: "conditional",
          };
        }
        return { label: "Processing", color: "#2196F3", action: "approve" };
      }
      if (request_status === 2) {
        // Mirror desktop: while Auto-Pay owns the accepted withdrawal (triggered and
        // not failed), block manual pay/reject and show the in-flight chip.
        const ps = String(row?.auto_pay_payout_status || "");
        const autoPayManaged = row?.auto_pay_triggered === true && ps !== "failed";
        if (autoPayManaged) {
          const inFlight = ps === "submitting" || ps === "submitted";
          return {
            label: inFlight ? "⏳ Bybit" : "⏳ Auto-Pay",
            color: "#1565C0",
            action: null,
          };
        }
        // Manual / failed payout: allow Mark Paid and (when applicable) Reject.
        const canReject = !row?.offered_amount && !row?.is_full_withdraw;
        return {
          label: "Accepted",
          color: "#4CAF50",
          action: "pay",
          canReject,
          payoutFailed: ps === "failed",
          payoutError: row?.auto_pay_payout_error,
        };
      }
      if (request_status === 3) {
        return {
          label: "Cancelled By User",
          color: "#F44336",
          action: null,
        };
      }
      if (request_status === 4) {
        return { label: "Rejected", color: "#D32F2D", action: null };
      }
      if (request_status === 5) {
        return { label: "Paid", color: "#4CAF50", action: null };
      }
      return { label: "Unknown", color: "#757575", action: null };
    }
  };

  const renderActionButtons = (row, statusInfo, isProcessing) => {
    const { action } = statusInfo;

    if (!action) return null;

    return (
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mt: 2,
          justifyContent: "center",
        }}
      >
        {action === "process" && onProcess && (
          <IconButton
            disabled={isProcessing}
            sx={{
              background: "#ff9800",
              color: "#fff",
              minWidth: "120px",
              height: "48px",
              borderRadius: "8px",
              "&:hover": {
                background: "#f57c00",
              },
            }}
            onClick={() => !isProcessing && onProcess(row)}
          >
            {isProcessing ? (
              <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
            ) : (
              <HourglassEmptyIcon sx={{ mr: 1 }} />
            )}
            {isProcessing ? "Processing..." : "Process"}
          </IconButton>
        )}

        {action === "approve" && onApprove && (
          <>
            <IconButton
              disabled={isProcessing}
              sx={{
                background: "#4caf50",
                color: "#fff",
                minWidth: "120px",
                height: "48px",
                borderRadius: "8px",
                "&:hover": {
                  background: "#388e3c",
                },
              }}
              onClick={() => !isProcessing && onApprove(row)}
            >
              {isProcessing ? (
                <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
              ) : (
                <CheckIcon sx={{ mr: 1 }} />
              )}
              {isProcessing ? "Processing..." : "Approve"}
            </IconButton>
            {onReject && (
              <IconButton
                disabled={isProcessing}
                sx={{
                  background: "#f44336",
                  color: "#fff",
                  minWidth: "120px",
                  height: "48px",
                  borderRadius: "8px",
                  "&:hover": {
                    background: "#d32f2f",
                  },
                }}
                onClick={() => !isProcessing && onReject(row)}
              >
                {isProcessing ? (
                  <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                ) : (
                  <CloseIcon sx={{ mr: 1 }} />
                )}
                {isProcessing ? "Processing..." : "Reject"}
              </IconButton>
            )}
          </>
        )}

        {action === "pay" && (
          <>
            {onPay && (
              <IconButton
                disabled={isProcessing}
                sx={{
                  background: "#4caf50",
                  color: "#fff",
                  minWidth: "120px",
                  height: "48px",
                  borderRadius: "8px",
                  "&:hover": {
                    background: "#388e3c",
                  },
                }}
                onClick={() => !isProcessing && onPay(row)}
              >
                {isProcessing ? (
                  <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                ) : (
                  <PaidIcon sx={{ mr: 1 }} />
                )}
                {isProcessing ? "Processing..." : "Mark Paid"}
              </IconButton>
            )}
            {statusInfo.canReject && onReject && (
              <IconButton
                disabled={isProcessing}
                sx={{
                  background: "#f44336",
                  color: "#fff",
                  minWidth: "120px",
                  height: "48px",
                  borderRadius: "8px",
                  "&:hover": {
                    background: "#d32f2f",
                  },
                }}
                onClick={() => !isProcessing && onReject(row)}
              >
                {isProcessing ? (
                  <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
                ) : (
                  <CloseIcon sx={{ mr: 1 }} />
                )}
                {isProcessing ? "Processing..." : "Reject"}
              </IconButton>
            )}
          </>
        )}

        {action === "conditional" && onConditionalOffer && (
          <IconButton
            disabled={isProcessing}
            sx={{
              background: "#FF9800",
              color: "#fff",
              minWidth: "140px",
              height: "48px",
              borderRadius: "8px",
              "&:hover": {
                background: "#F57C00",
              },
            }}
            onClick={() => !isProcessing && onConditionalOffer(row)}
          >
            {isProcessing ? (
              <CircularProgress size={18} sx={{ color: "#fff", mr: 1 }} />
            ) : (
              <PaidIcon sx={{ mr: 1 }} />
            )}
            {isProcessing ? "Processing..." : "Send Offer"}
          </IconButton>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        p: 2,
        pb: 4,
      }}
    >
      {rows.map((row) => {
        const statusInfo = getStatusInfo(row);
        const amount = parseFloat(row?.total_amount) || 0;
        const isFullWithdraw = row?.is_full_withdraw === true;
        const rowKey = row?.doc_id || row?.id;
        const isProcessing = processingId === rowKey;

        return (
          <SwipeableCard
            key={row?.id || row?.doc_id}
            onSwipeLeft={() => {
              if (isProcessing) return;
              // Swipe left = Approve
              if (statusInfo.action === "approve" && onApprove) {
                onApprove(row);
              } else if (statusInfo.action === "pay" && onPay) {
                onPay(row);
              }
            }}
            onSwipeRight={() => {
              if (isProcessing) return;
              // Swipe right = Reject
              if (statusInfo.action === "approve" && onReject) {
                onReject(row);
              }
            }}
          >
            <Card
              sx={{
                width: "100%",
                boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                borderRadius: "12px",
                overflow: "visible",
              }}
            >
              <CardContent sx={{ p: 2.5 }}>
                {/* Header: Name and Status */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 1.5,
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontSize: "18px",
                        fontWeight: 600,
                        mb: 0.5,
                      }}
                    >
                      {row?.user_name || "Unknown"}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ fontSize: "14px" }}
                    >
                      {row?.email || "--"}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Chip
                      label={isProcessing ? "Processing..." : statusInfo.label}
                      sx={{
                        backgroundColor: isProcessing ? "#607d8b" : statusInfo.color,
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: "12px",
                        height: "28px",
                      }}
                    />
                    {isCapitalRoutingRow(row) && (
                      <CapitalRoutingChip routingId={row?.capital_routing_id} />
                    )}
                  </Box>
                </Box>

                {/* Amount */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: "24px",
                      fontWeight: 700,
                      color: type === "deposit" ? "#4CAF50" : "#2196F3",
                    }}
                  >
                    ${formatAmount(amount)}
                    {isFullWithdraw && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          ml: 1,
                          fontSize: "12px",
                          color: "text.secondary",
                        }}
                      >
                        (Full Withdraw)
                      </Typography>
                    )}
                  </Typography>
                </Box>

                {/* Date */}
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Date:</strong> {formatDate(row?.date)}
                  </Typography>
                </Box>

                {/* Wallet (withdrawals only) */}
                {type === "withdrawal" && (
                  <Box sx={{ mb: 1.5 }}>
                    {isCapitalRoutingRow(row) ? (
                      <Typography variant="body2" color="text.secondary" component="div">
                        <strong>Wallet:</strong>{" "}
                        <Tooltip title="Capital Routing — internal transfer to another Fiddux account (no on-chain payout).">
                          <Chip
                            label="Internal routing"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 22 }}
                          />
                        </Tooltip>
                      </Typography>
                    ) : row?.destination_type === "fiat_manual" ? (
                      <Typography variant="body2" color="text.secondary" component="div">
                        <strong>Wallet:</strong>{" "}
                        <Tooltip title="Paid in FIAT manually by Fiddux — process outside crypto (no on-chain payout).">
                          <Chip
                            label="FIAT manual"
                            size="small"
                            color="warning"
                            variant="outlined"
                            sx={{ height: 22 }}
                          />
                        </Tooltip>
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Wallet:</strong>{" "}
                        <Tooltip title={getWithdrawalWalletTooltip(row)}>
                          <span style={{ color: "inherit" }}>
                            {getWithdrawalWalletDisplay(row)}
                          </span>
                        </Tooltip>
                      </Typography>
                    )}
                  </Box>
                )}

                {/* Notes */}
                {row?.notes && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      backgroundColor: "#f5f5f5",
                      borderRadius: "8px",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ fontSize: "13px", color: "#424242" }}
                    >
                      <strong>Notes:</strong> {row?.notes || "--"}
                    </Typography>
                  </Box>
                )}

                {/* Receipt (for deposits) */}
                {type === "deposit" && row?.payment_receipt && (
                  <Box sx={{ mb: 1.5 }}>
                    <Tooltip title="Download Receipt">
                      <IconButton
                        onClick={() => {
                          const url = row?.payment_receipt?.replace(/fiddux-5a1f1\.appspot\.com/g, "fidduxai-rowy.appspot.com") || row?.payment_receipt;
                          window.open(url, "_blank");
                        }}
                        sx={{
                          color: "#2196F3",
                          border: "1px solid #2196F3",
                          borderRadius: "8px",
                        }}
                      >
                        <ReceiptIcon sx={{ mr: 1 }} />
                        <Typography variant="body2">View Receipt</Typography>
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* TXN ID */}
                {row?.txn_id && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      <strong>TXN:</strong> {row?.txn_id}
                    </Typography>
                  </Box>
                )}

                {/* E10: On-chain explorer link (auto-pay payouts) */}
                {type === "withdrawal" && getExplorerTxUrl(row) && (
                  <Box sx={{ mb: 1.5 }}>
                    <IconButton
                      component="a"
                      href={getExplorerTxUrl(row)}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: "#4CAF50",
                        border: "1px solid #4CAF50",
                        borderRadius: "8px",
                      }}
                    >
                      <OpenInNewIcon sx={{ mr: 1, fontSize: 18 }} />
                      <Typography variant="body2">View on-chain</Typography>
                    </IconButton>
                  </Box>
                )}

                {/* Bybit payout failed warning (mirror desktop) */}
                {type === "withdrawal" && statusInfo.payoutFailed && (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      backgroundColor: "rgba(230,81,0,0.12)",
                      border: "1px solid #E65100",
                      borderRadius: "8px",
                      display: "flex",
                      gap: 1,
                    }}
                  >
                    <WarningAmberIcon sx={{ color: "#E65100", fontSize: 20, flexShrink: 0, mt: "2px" }} />
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: "#E65100", mb: 0.25 }}>
                        Bybit payout failed
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", color: "text.secondary" }}>
                        {statusInfo.payoutError ||
                          "Auto-pay could not send via Bybit (insufficient balance or API error)."}
                      </Typography>
                      <Typography variant="caption" sx={{ display: "block", mt: 0.5, color: "#E65100", fontWeight: 600 }}>
                        Mark as Paid after manual transfer, or Reject to restore the client balance.
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Action Buttons */}
                {renderActionButtons(row, statusInfo, isProcessing)}
              </CardContent>
            </Card>
          </SwipeableCard>
        );
      })}
    </Box>
  );
};

export default MobileCardView;
